import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/Dialog';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Select } from './ui/Select';
import { Projekt } from '../types';
import { Check } from 'lucide-react';
import { cn } from '../lib/utils';
interface AddProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (project: Omit<Projekt, 'id'>) => void;
}
export function AddProjectDialog({
  open,
  onOpenChange,
  onAdd
}: AddProjectDialogProps) {
  const [formData, setFormData] = useState({
    projektnummer: '',
    name: '',
    kunde: '',
    startKw: 1,
    endKw: 10,
    status: 'Aktiv',
    farbe: '#3B82F6',
    phase: 'Konzept'
  });
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6', '#EC4899', '#6366F1' // Indigo
  ];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData as any);
    onOpenChange(false);
    setFormData({
      projektnummer: '',
      name: '',
      kunde: '',
      startKw: 1,
      endKw: 10,
      status: 'Aktiv',
      farbe: '#3B82F6',
      phase: 'Konzept'
    });
  };
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-43">
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden gap-0 border-none shadow-2xl" data-id="element-44">
        <div className="bg-muted/30 p-6 border-b" data-id="element-45">
          <DialogHeader data-id="element-46">
            <DialogTitle className="text-xl font-bold tracking-tight" data-id="element-47">
              Neues Projekt anlegen
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1" data-id="element-48">
              Erstellen Sie ein neues Projekt für die Ressourcenplanung.
            </p>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-49">
          <div className="grid grid-cols-2 gap-4" data-id="element-50">
            <div className="space-y-2" data-id="element-51">
              <Label htmlFor="pnr" className="text-xs font-bold uppercase text-muted-foreground tracking-wider" data-id="element-52">
                Projektnummer
              </Label>
              <Input id="pnr" value={formData.projektnummer} onChange={e => setFormData({
              ...formData,
              projektnummer: e.target.value
            })} placeholder="P-2024-XXX" className="bg-muted/10" required data-id="element-53" />
            </div>
            <div className="space-y-2" data-id="element-54">
              <Label htmlFor="kunde" className="text-xs font-bold uppercase text-muted-foreground tracking-wider" data-id="element-55">
                Kunde
              </Label>
              <Input id="kunde" value={formData.kunde} onChange={e => setFormData({
              ...formData,
              kunde: e.target.value
            })} placeholder="Firmenname" className="bg-muted/10" required data-id="element-56" />
            </div>
          </div>

          <div className="space-y-2" data-id="element-57">
            <Label htmlFor="name" className="text-xs font-bold uppercase text-muted-foreground tracking-wider" data-id="element-58">
              Projektname
            </Label>
            <Input id="name" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} placeholder="Anlagenbezeichnung" className="bg-muted/10" required data-id="element-59" />
          </div>

          <div className="space-y-2" data-id="element-60">
            <Label className="text-xs font-bold uppercase text-muted-foreground tracking-wider" data-id="element-61">
              Zeitraum (KW)
            </Label>
            <div className="flex items-center gap-3" data-id="element-62">
              <div className="relative flex-1" data-id="element-63">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-bold" data-id="element-64">
                  VON
                </span>
                <Input type="number" min="1" max="52" value={formData.startKw} onChange={e => setFormData({
                ...formData,
                startKw: parseInt(e.target.value)
              })} className="pl-10 bg-muted/10" data-id="element-65" />
              </div>
              <span className="text-muted-foreground" data-id="element-66">→</span>
              <div className="relative flex-1" data-id="element-67">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-bold" data-id="element-68">
                  BIS
                </span>
                <Input type="number" min="1" max="52" value={formData.endKw} onChange={e => setFormData({
                ...formData,
                endKw: parseInt(e.target.value)
              })} className="pl-10 bg-muted/10" data-id="element-69" />
              </div>
            </div>
          </div>

          <div className="space-y-3" data-id="element-70">
            <Label htmlFor="farbe" className="text-xs font-bold uppercase text-muted-foreground tracking-wider" data-id="element-71">
              Projektfarbe
            </Label>
            <div className="flex flex-wrap gap-3" data-id="element-72">
              {colors.map(color => <button key={color} type="button" className={cn('w-8 h-8 rounded-full cursor-pointer transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 flex items-center justify-center shadow-sm', formData.farbe === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'border border-transparent')} style={{
              backgroundColor: color
            }} onClick={() => setFormData({
              ...formData,
              farbe: color
            })} data-id="element-73">
                  {formData.farbe === color && <Check className="w-4 h-4 text-white drop-shadow-md" data-id="element-74" />}
                </button>)}
            </div>
          </div>
        </form>

        <div className="bg-muted/30 p-4 border-t flex justify-end gap-3" data-id="element-75">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-id="element-76">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} className="px-6" data-id="element-77">
            Projekt erstellen
          </Button>
        </div>
      </DialogContent>
    </Dialog>;
}